import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthContext.js";
import { RequireRole } from "./auth/guards.js";
import { rutaInicioPorRol } from "./auth/roles.js";
import { Loader } from "./components/Loader.js";
import { AdminLayout } from "./features/admin/AdminLayout.js";
import { AdminDashboard } from "./features/admin/AdminDashboard.js";
import { AdminUsers } from "./features/admin/AdminUsers.js";
import { AdminChildren } from "./features/admin/AdminChildren.js";
import { AdminChildDetail } from "./features/admin/AdminChildDetail.js";
import { AdminBaterias } from "./features/admin/AdminBaterias.js";
import { AdminBateriaDetail } from "./features/admin/AdminBateriaDetail.js";
import { AdminBanco } from "./features/admin/AdminBanco.js";
import { AdminBancoDetail } from "./features/admin/AdminBancoDetail.js";
import { AdminLicencias } from "./features/admin/AdminLicencias.js";
import { AdminLicenciaDetail } from "./features/admin/AdminLicenciaDetail.js";
import { AdminPagosOffline } from "./features/admin/AdminPagosOffline.js";
import { AdminAsignaciones } from "./features/admin/AdminAsignaciones.js";
import { AdminPlanes } from "./features/admin/AdminPlanes.js";
import { AdminConfig } from "./features/admin/AdminConfig.js";
import { LoginPage } from "./features/auth/LoginPage.js";
import { RegisterPage } from "./features/auth/RegisterPage.js";
import { ParentDashboard } from "./features/parent/ParentDashboard.js";
import { NewChildPage } from "./features/parent/NewChildPage.js";
import { ConsentsPage } from "./features/parent/ConsentsPage.js";
import { PhotosPage } from "./features/parent/PhotosPage.js";
import { StartEvalPage } from "./features/parent/StartEvalPage.js";
import { ParentReportPage } from "./features/parent/ParentReportPage.js";
import { ChildHistoryPage } from "./features/parent/ChildHistoryPage.js";
import { PracticePage } from "./features/parent/PracticePage.js";
import { TherapistLayout } from "./features/therapist/TherapistLayout.js";
import { TherapistDashboard } from "./features/therapist/TherapistDashboard.js";
import { LinkChildPage } from "./features/therapist/LinkChildPage.js";
import { TherapistPatientsPage } from "./features/therapist/TherapistPatientsPage.js";
import { EvaluationsPage } from "./features/therapist/EvaluationsPage.js";
import { NewPatientPage } from "./features/therapist/NewPatientPage.js";
import { PatientDetailPage } from "./features/therapist/PatientDetailPage.js";
import { CustomTestsPage } from "./features/therapist/CustomTestsPage.js";
import { TherapistReportPage } from "./features/therapist/TherapistReportPage.js";
import { WorkbenchPage } from "./features/therapist/WorkbenchPage.js";
import { CreditsPage } from "./features/therapist/CreditsPage.js";
import { ProfilePage } from "./features/therapist/ProfilePage.js";
import { MarketplacePage } from "./features/marketplace/MarketplacePage.js";
import { NotFoundPage } from "./features/NotFoundPage.js";

function RootRedirect() {
  const { usuario, cargando } = useAuth();
  if (cargando) return <Loader />;
  if (!usuario) return <Navigate to="/login" replace />;
  return <Navigate to={rutaInicioPorRol(usuario.rol)} replace />;
}

/** Marco "device" (mobile-first, máx 480px) para las apps de padre/terapeuta.
 *  El panel admin NO lo usa: es una herramienta interna a ancho completo. */
function DeviceShell() {
  return (
    <div className="app-shell">
      <div className="app-viewport">
        <Outlet />
      </div>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      {/* ---- panel admin (ancho completo, fuera del device) ---- */}
      <Route
        path="/admin"
        element={
          <RequireRole permitidos={["admin"]}>
            <AdminLayout />
          </RequireRole>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="usuarios" element={<AdminUsers />} />
        <Route path="ninos" element={<AdminChildren />} />
        <Route path="ninos/:childId" element={<AdminChildDetail />} />
        <Route path="evaluaciones" element={<AdminBaterias />} />
        <Route path="evaluaciones/:bateriaId" element={<AdminBateriaDetail />} />
        <Route path="banco" element={<AdminBanco />} />
        <Route path="banco/:id" element={<AdminBancoDetail />} />
        <Route path="creditos" element={<AdminLicencias />} />
        <Route path="creditos/pagos-offline" element={<AdminPagosOffline />} />
        <Route path="creditos/asignaciones" element={<AdminAsignaciones />} />
        <Route path="creditos/paquetes" element={<AdminPlanes />} />
        <Route path="creditos/:id" element={<AdminLicenciaDetail />} />
        <Route path="config" element={<AdminConfig />} />
      </Route>

      {/* ---- resto de la app (dentro del marco device) ---- */}
      <Route element={<DeviceShell />}>
        <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/terapeutas" element={<MarketplacePage />} />

          {/* ---- padre ---- */}
          <Route
            path="/padre"
            element={
              <RequireRole permitidos={["padre"]}>
                <ParentDashboard />
              </RequireRole>
            }
          />
          <Route
            path="/padre/nuevo"
            element={
              <RequireRole permitidos={["padre"]}>
                <NewChildPage />
              </RequireRole>
            }
          />
          <Route
            path="/padre/nino/:childId/consentimientos"
            element={
              <RequireRole permitidos={["padre"]}>
                <ConsentsPage />
              </RequireRole>
            }
          />
          <Route
            path="/padre/nino/:childId/fotos"
            element={
              <RequireRole permitidos={["padre"]}>
                <PhotosPage />
              </RequireRole>
            }
          />
          <Route
            path="/padre/nino/:childId/evaluar"
            element={
              <RequireRole permitidos={["padre"]}>
                <StartEvalPage />
              </RequireRole>
            }
          />
          <Route
            path="/padre/reporte/:sesionId"
            element={
              <RequireRole permitidos={["padre"]}>
                <ParentReportPage />
              </RequireRole>
            }
          />
          <Route
            path="/padre/nino/:childId/historial"
            element={
              <RequireRole permitidos={["padre"]}>
                <ChildHistoryPage />
              </RequireRole>
            }
          />
          <Route
            path="/padre/nino/:childId/practica"
            element={
              <RequireRole permitidos={["padre"]}>
                <PracticePage />
              </RequireRole>
            }
          />

          {/* ---- terapeuta: administración inmersiva (SIN tab bar) ----
              El reproductor de la batería es pantalla completa (como el juego del
              niño): el tab bar molestaría o navegaría por accidente a mitad de una
              grabación. Su salida es role-aware (vuelve a la ficha). */}
          <Route
            path="/terapeuta/paciente/:childId/evaluar"
            element={
              <RequireRole permitidos={["terapeuta"]}>
                <StartEvalPage />
              </RequireRole>
            }
          />

          {/* ---- terapeuta: panel con bottom tab bar persistente ----
              TherapistLayout monta el chrome (RequireRole + tab bar); las hijas
              solo renderizan su ScreenHeader + contenido. Sin callejones. */}
          <Route element={<TherapistLayout />}>
            <Route path="/terapeuta" element={<TherapistDashboard />} />
            <Route path="/terapeuta/vincular" element={<LinkChildPage />} />
            <Route path="/terapeuta/pacientes" element={<TherapistPatientsPage />} />
            <Route path="/terapeuta/evaluaciones" element={<EvaluationsPage />} />
            <Route path="/terapeuta/paciente/nuevo" element={<NewPatientPage />} />
            <Route path="/terapeuta/paciente/:childId" element={<PatientDetailPage />} />
            <Route path="/terapeuta/pruebas" element={<CustomTestsPage />} />
            <Route path="/terapeuta/reporte/:sesionId" element={<TherapistReportPage />} />
            <Route path="/terapeuta/revision/:bateriaId" element={<WorkbenchPage />} />
            <Route path="/terapeuta/creditos" element={<CreditsPage />} />
            <Route path="/terapeuta/perfil" element={<ProfilePage />} />
          </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
